import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { STORAGE_KEYS } from '@/shared/constants';
import { parseStepReference } from '@/shared/utils/step-reference';

/**
 * Where a snippet's text sits in the document it came from, in plain-text
 * coordinates so it survives both a reload and the document being replaced.
 * Backs the blueprint's highlight behind the chip; see
 * `@/modules/automation-run/extensions/selection-text-range`.
 *
 * Snippets that aren't a range of a document carry none — a step reference
 * added from `ManifestStepCard`, for one.
 */
export interface ISelectionSnippetAnchor {
  /** Character offset of the range's start in the document's text form. */
  start: number;
  /**
   * The document's own text for the range, which is not always the snippet's
   * text: a table selection's snippet joins its cells with `|` for the agent
   * to read, and that reshaped text can't be found in the document again.
   */
  text: string;
}

/** A single snippet of preview text the user "Selected" into the composer. */
export interface ISelectionSnippet {
  id: string;
  text: string;
  /** Present only for snippets taken from a document range. */
  anchor?: ISelectionSnippetAnchor;
}

/**
 * Returned whenever the slot holds nothing for this composer. A shared constant
 * rather than a fresh `[]`, so consumers deriving from `snippets` (the
 * blueprint's highlight effect) don't see a new identity on every render.
 */
const NO_SNIPPETS: ISelectionSnippet[] = [];

interface ISelectionContextSlot {
  /** The composer (Rain thread id) that owns these snippets. */
  scopeId: string;
  snippets: ISelectionSnippet[];
}

export interface IUseSelectionContextReturn {
  /** Snippets belonging to `scopeId` (empty for any other composer). */
  snippets: ISelectionSnippet[];
  /** Append a snippet (trimmed; blanks ignored) and return its stable id. */
  addSnippet: (
    text: string,
    anchor?: ISelectionSnippetAnchor,
  ) => string | undefined;
  /** Remove one snippet by id (the chip's cancel affordance). */
  removeSnippet: (id: string) => void;
  /** Drop them all (called after a send folds them into the message). */
  clear: () => void;
}

/**
 * Shared, cross-component store for "Select"-ed preview snippets, mirroring
 * {@link usePersistedDraftPrompt}: one localStorage slot scoped by composer id,
 * so the editor's bubble-menu action (writer) and the chat composer
 * (reader/renderer) share the same list. Opening a different composer sees a
 * different `scopeId` and shows nothing.
 *
 * @param {string | undefined} scopeId - Composer id; undefined disables it.
 * @returns {IUseSelectionContextReturn} snippets + add/remove/clear
 */
export function useSelectionContext(
  scopeId: string | undefined,
): IUseSelectionContextReturn {
  const [slot, setSlot] = useLocalStorage<ISelectionContextSlot | null>(
    STORAGE_KEYS.DRAFTS_EDITOR_SELECTION_CONTEXT,
    null,
  );

  const snippets =
    scopeId && slot?.scopeId === scopeId ? slot.snippets : NO_SNIPPETS;

  const addSnippet = useCallback(
    (text: string, anchor?: ISelectionSnippetAnchor) => {
      const trimmed = text.trim();
      if (!scopeId || !trimmed) return;
      const id = crypto.randomUUID();
      setSlot(prev => {
        const existing = prev?.scopeId === scopeId ? prev.snippets : [];
        return {
          scopeId,
          snippets: [...existing, { id, text: trimmed, anchor }],
        };
      });
      return id;
    },
    [scopeId, setSlot],
  );

  const removeSnippet = useCallback(
    (id: string) => {
      if (!scopeId) return;
      setSlot(prev => {
        if (prev?.scopeId !== scopeId) return prev;
        const next = prev.snippets.filter(s => s.id !== id);
        return next.length ? { scopeId, snippets: next } : null;
      });
    },
    [scopeId, setSlot],
  );

  const clear = useCallback(() => {
    if (!scopeId) return;
    setSlot(prev => (prev?.scopeId === scopeId ? null : prev));
  }, [scopeId, setSlot]);

  return { snippets, addSnippet, removeSnippet, clear };
}

/**
 * Folds "Selected"-from-preview (or added-from-step) snippets into an
 * outgoing message, so the agent receives the referenced content alongside
 * the user's typed instruction. Snippets lead; the user's typed instruction
 * (if any) follows.
 *
 * A step-reference snippet (its text is a `<step_reference>` tag, added by
 * `ManifestStepCard`'s "add to chat" button) is folded in raw — quoting it
 * would break `extractStepReferences`' exact-reconstruction on the history
 * side. Anything else (a text-selection snippet from the markdown editor's
 * bubble menu) is still quoted as markdown, since it's just prose the agent
 * should read as quoted context, not a tag to parse back out.
 */
export function foldSelectionSnippets(
  text: string,
  snippets: ISelectionSnippet[],
): string {
  if (snippets.length === 0) return text;
  const quoted = snippets
    .map(s =>
      parseStepReference(s.text)
        ? s.text
        : s.text
            .split('\n')
            .map(line => `> ${line}`)
            .join('\n'),
    )
    .join('\n\n');
  const body = text.trim();
  return body ? `${quoted}\n\n${body}` : quoted;
}

/**
 * The user's typed instruction from a message {@link foldSelectionSnippets}
 * built, without the snippets it led with.
 *
 * @param content - The folded message.
 * @param snippets - The snippets that were folded into it.
 * @returns The typed instruction, trimmed; empty when only snippets were sent.
 */
export function unfoldSelectionSnippets(
  content: string,
  snippets: ISelectionSnippet[],
): string {
  const quoted = foldSelectionSnippets('', snippets);
  return (
    content.startsWith(quoted) ? content.slice(quoted.length) : content
  ).trim();
}
